// @ts-nocheck
/**
 * modeDialogMethods.ts
 * モードダイアログ関連メソッドとマップ速度プロファイル
 * App.vue から切り出し
 */
import { tuneMapInteractionSpeed } from '@/js/utils/map-speed-tuning'
import { startAreaAnalysisFreehand } from '@/js/areaAnalyzer'

type VM = any

// ズーム／パン速度のプリセット値。体感速度を調整したい場合はここを編集する。
export const MAP_SPEED_PRESETS = Object.freeze({
  ios: {
    verySlow: {
      wheelZoomRate: 1 / 520,
      trackpadZoomRate: 1.02,
      touchZoomRate: 1.02,
      keyboardPanOffset: 50,
      keyboardZoomRate: 1.05,
    },
    slow: {
      wheelZoomRate: 1 / 420,
      trackpadZoomRate: 1.08,
      touchZoomRate: 1.05,
      keyboardPanOffset: 70,
      keyboardZoomRate: 1.12,
    },
    normal: {
      wheelZoomRate: 1 / 360,
      trackpadZoomRate: 1.18,
      touchZoomRate: 1.1,
      keyboardPanOffset: 90,
      keyboardZoomRate: 1.22,
    },
    fast: {
      wheelZoomRate: 1 / 320,
      trackpadZoomRate: 1.28,
      touchZoomRate: 1.14,
      keyboardPanOffset: 120,
      keyboardZoomRate: 1.33,
    },
    veryFast: {
      wheelZoomRate: 1 / 280,
      trackpadZoomRate: 1.4,
      touchZoomRate: 1.18,
      keyboardPanOffset: 140,
      keyboardZoomRate: 1.45,
    },
  },
  desktop: {
    verySlow: {
      wheelZoomRate: 1 / 700,
      trackpadZoomRate: 1 / 240,
      touchZoomRate: 1.02,
      keyboardPanOffset: 50,
      keyboardZoomRate: 1.05,
    },
    slow: {
      wheelZoomRate: 1 / 540,
      trackpadZoomRate: 1 / 160,
      touchZoomRate: 1.05,
      keyboardPanOffset: 70,
      keyboardZoomRate: 1.1,
    },
    normal: {
      wheelZoomRate: 1 / 450,
      trackpadZoomRate: 1 / 100,
      touchZoomRate: 1.1,
      keyboardPanOffset: 100,
      keyboardZoomRate: 1.2,
    },
    fast: {
      wheelZoomRate: 1 / 300,
      trackpadZoomRate: 0.8,
      touchZoomRate: 1.2,
      keyboardPanOffset: 120,
      keyboardZoomRate: 1.33,
    },
    veryFast: {
      wheelZoomRate: 1 / 220,
      trackpadZoomRate: 1.6,
      touchZoomRate: 1.32,
      keyboardPanOffset: 140,
      keyboardZoomRate: 1.45,
    },
  },
})

// --- 面積分析モードダイアログ ---
export function openAreaAnalysisModeDialog(vm: VM) {
  vm.enableAreaAnalysisEvacuationLayerOnce()
  vm.showAreaAnalysisModeDialog = true
}

export function cancelAreaAnalysisModeDialog(vm: VM) {
  vm.showAreaAnalysisModeDialog = false
  vm.removeSelectedLayerById('oh-reinfolib-hinanbasho')
}

// 災害シミュレーションダイアログを開く（許可ユーザー限定）
export function openDisasterSimulationDialog(vm: VM) {
  vm.enableAreaAnalysisEvacuationLayerOnce()
  vm.pendingOpenSimulationDialog = true
  vm.beginAreaAnalysisPointSelection()
}

// --- 断面図 モード選択 ---
export function openElevationProfileModeDialog(vm: VM) {
  vm.showElevationProfileModeDialog = true
}

export function selectElevationProfileMode(vm: VM, mode: string) {
  vm.showElevationProfileModeDialog = false
  if (mode === 'line') {
    vm.$store.state.elevationDrawMode = 'line'
    vm.$nextTick(() => vm.toggleLDrawLine())
  } else if (mode === 'free') {
    vm.pendingFreehandSource = 'modeDialog'
    vm.showFreehandInfoDialog = true
  }
}

export function confirmFreehandDraw(vm: VM) {
  vm.showFreehandInfoDialog = false
  vm.$store.state.elevationDrawMode = 'free'
  vm.$nextTick(() => vm.toggleLDrawFree())
}

// --- AI人口プロファイル モード選択 ---
export function openPopulationProfileModeDialog(vm: VM) {
  vm.showPopulationProfileModeDialog = true
}

export function cancelPopulationProfileModeDialog(vm: VM) {
  vm.showPopulationProfileModeDialog = false
}

export async function selectPopulationProfileMode(vm: VM, mode: string) {
  vm.showPopulationProfileModeDialog = false
  if (mode === 'point') {
    vm.$store.commit('addFWInstance', {
      type: 'super-population',
      data: { analysisMode: 'point' }
    })
    return
  }
  if (mode === 'freehand') {
    await startPopulationProfileFreehandFlow(vm)
    return
  }
  if (mode === 'circle') {
    await startPopulationProfileCircleFlow(vm)
    return
  }
  if (mode === 'polygon') {
    await vm.startPopulationProfilePolygonFlow()
    return
  }
}

export async function startPopulationProfileFreehandFlow(vm: VM) {
  const map = vm.$store.state.map01
  if (!map) return
  const polygon = await startAreaAnalysisFreehand(map)
  if (polygon) {
    vm.$store.commit('addFWInstance', {
      type: 'super-population',
      data: { polygonData: polygon, analysisMode: 'freehand' },
    })
  }
}

export async function startPopulationProfileCircleFlow(vm: VM) {
  const map = vm.$store.state.map01
  if (!map) return
  const { startAreaAnalysisCircle } = await import('@/js/areaAnalyzer')
  const polygon = await startAreaAnalysisCircle(map)
  if (polygon) {
    vm.$store.commit('addFWInstance', {
      type: 'super-population',
      data: { polygonData: polygon, analysisMode: 'circle' },
    })
  }
}

// --- マップ速度プロファイル ---
export function getMapSpeedOverrides(vm: VM, profile: string) {
  const isIOS = vm.$store.state.isIOS
  const platformPresets = isIOS ? MAP_SPEED_PRESETS.ios : MAP_SPEED_PRESETS.desktop
  return { ...((platformPresets[profile]) || platformPresets.normal) }
}

export function applyMapSpeedProfile(vm: VM, profile: string) {
  const mapNames = ['map01', 'map02']
  mapNames.forEach((mapName) => {
    const map = vm.$store.state[mapName]
    if (!map) return
    if (vm.mapSpeedTuners?.[mapName]) {
      try { vm.mapSpeedTuners[mapName]() } catch (_) {}
      vm.mapSpeedTuners[mapName] = null
    }
    const overrides = getMapSpeedOverrides(vm, profile)
    vm.mapSpeedTuners[mapName] = tuneMapInteractionSpeed(map, overrides)
  })
}
